import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Container,
    Breadcrumbs,
    Link,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Button,
    Divider,
    Card,
    CardContent,
    IconButton,
    Tooltip,
    Alert,
    useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { 
    Sort as SortIcon, 
    FilterAlt as FilterAltIcon,
    Refresh as RefreshIcon,
    Home as HomeIcon,
    HistoryEdu as HistoryIcon,
    Search as SearchIcon,
    ClearAll as ClearAllIcon,
    InfoOutlined as InfoOutlinedIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { publicInstance } from '../axiosInstance';
import { useRefresh } from '../../Components/RefreshContext';
import { getOperatorColor } from './utils';

// Define interfaces for data types
interface TimeStamp {
    time: string;
    event: string;
}

interface HistoricalDeviceInfo {
    id: number;
    box: string;
    iccid: string;
    time_stamp?: string;
    operator?: string;
    mobile_operator?: string;
    label?: Array<{ color: string | null, label: string }>;
    secondaryGroup?: string;
    modem?: string;
    time_stamps?: TimeStamp[];
    comments?: string | null;
}

// Format date helper function
const formatCompactDate = (dateString: string) => {
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }).replace(',', ' ');
    } catch (error) {
        return 'Invalid date';
    }
};

const InventoryHistory: React.FC = () => {
    const theme = useTheme();
    const { refresh } = useRefresh();
    
    // State variables
    const [historicalDevices, setHistoricalDevices] = useState<HistoricalDeviceInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [boxFilter, setBoxFilter] = useState('');
    const [operatorFilter, setOperatorFilter] = useState<string>('');
    const [sortField, setSortField] = useState<keyof HistoricalDeviceInfo>('id');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

    // Load data on component mount
    useEffect(() => {
        fetchHistoricalData();
    }, [refresh]);

    // Fetch historical data
    const fetchHistoricalData = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await publicInstance.get('/view_dacapi_inventory_historical_all');
            if (response.status === 200) {
                setHistoricalDevices(response.data);
            } else {
                throw new Error('Failed to fetch historical data');
            }
        } catch (error) {
            console.error('Error fetching historical data:', error);
            setError('Failed to load historical data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Filter devices based on filters
    const filteredDevices = useMemo(() => {
        return historicalDevices.filter(device => {
            // Filter by box number
            if (boxFilter && !device.box.toString().includes(boxFilter)) {
                return false;
            }
            
            // Filter by operator
            if (operatorFilter && device.mobile_operator !== operatorFilter && device.operator !== operatorFilter) {
                return false;
            }
            
            return true;
        });
    }, [historicalDevices, boxFilter, operatorFilter]);

    // Get unique operators for the operator filter
    const uniqueOperators = useMemo(() => {
        const operators = new Set<string>();
        historicalDevices.forEach(device => {
            if (device.mobile_operator) {
                operators.add(device.mobile_operator);
            } else if (device.operator) {
                operators.add(device.operator);
            }
        });
        return Array.from(operators).filter(Boolean);
    }, [historicalDevices]);

    // Reset filters
    const resetFilters = () => {
        setBoxFilter('');
        setOperatorFilter('');
        setPage(0);
    };

    // Sorting function for table
    const sortDevices = (devices: HistoricalDeviceInfo[], field: keyof HistoricalDeviceInfo, direction: 'asc' | 'desc') => {
        return [...devices].sort((a, b) => {
            let aValue = a[field];
            let bValue = b[field];
            
            // Handle null/undefined values
            if (aValue === null || aValue === undefined) return direction === 'asc' ? -1 : 1;
            if (bValue === null || bValue === undefined) return direction === 'asc' ? 1 : -1;
            
            // Special handling for box field (convert to number)
            if (field === 'box') {
                const aNum = parseInt(String(aValue));
                const bNum = parseInt(String(bValue));
                return direction === 'asc' ? aNum - bNum : bNum - aNum;
            }
            
            // Special handling for time_stamp field
            if (field === 'time_stamp') {
                const aDate = aValue ? new Date(aValue as string).getTime() : 0;
                const bDate = bValue ? new Date(bValue as string).getTime() : 0;
                return direction === 'asc' ? aDate - bDate : bDate - aDate;
            }
            
            // Generic string comparison
            const aStr = String(aValue).toLowerCase();
            const bStr = String(bValue).toLowerCase();
            return direction === 'asc' 
                ? aStr.localeCompare(bStr)
                : bStr.localeCompare(aStr);
        });
    };

    // Create sorted and filtered devices
    const sortedAndFilteredDevices = useMemo(() => {
        return sortDevices(filteredDevices, sortField, sortDirection);
    }, [filteredDevices, sortField, sortDirection]);

    // Handle sorting change
    const handleSortChange = (field: keyof HistoricalDeviceInfo) => {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            // New field, default to descending
            setSortField(field);
            setSortDirection('desc');
        }
        setPage(0);
    };

    // Create sort button
    const renderSortButton = (field: keyof HistoricalDeviceInfo, label: string) => {
        const isActive = sortField === field;
        return (
            <Box 
                sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    fontWeight: isActive ? 'bold' : 'normal',
                    color: isActive ? theme.palette.primary.main : 'inherit'
                }}
                onClick={() => handleSortChange(field)}
            >
                {label}
                {isActive && (
                    <SortIcon 
                        fontSize="small" 
                        sx={{ 
                            ml: 0.5,
                            transform: sortDirection === 'asc' ? 'rotate(180deg)' : 'none'
                        }}
                    />
                )}
            </Box>
        );
    };

    // Handle page change
    const handlePageChange = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Render label chips
    const renderLabels = (labels: Array<{ color: string | null, label: string }> | null) => {
        if (!labels || labels.length === 0) return '-';
        
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {labels.map((label, index) => (
                    <Chip
                        key={index}
                        label={label.label}
                        size="small"
                        sx={{
                            backgroundColor: label.color ? alpha(label.color, theme.name === 'dark' ? 0.2 : 0.1) : undefined,
                            borderColor: label.color || undefined,
                            color: label.color || (theme.name === 'dark' ? theme.palette.primary.light : theme.palette.primary.main),
                            borderWidth: 1,
                            borderStyle: 'solid'
                        }}
                    />
                ))}
            </Box>
        );
    };

    // Render timestamps
    const renderTimeStamps = (timestamps: TimeStamp[] | null) => {
        if (!timestamps || timestamps.length === 0) return '-';
        
        // Sort timestamps in reverse chronological order (newest first)
        const sortedTimestamps = [...timestamps].sort((a, b) => {
            return new Date(b.time).getTime() - new Date(a.time).getTime();
        });
        
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {sortedTimestamps.map((ts, index) => (
                    <Box 
                        key={index} 
                        sx={{ 
                            p: 0.5, 
                            borderRadius: 1,
                            backgroundColor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.03)' : 'transparent',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderLeft: '3px solid',
                            borderColor: ts.event.includes('added') ? 'success.main' :
                                      ts.event.includes('moved') ? 'info.main' :
                                      ts.event.includes('returned') ? 'secondary.main' :
                                      ts.event.includes('mistake') ? 'error.main' :
                                      ts.event.includes('sent') ? 'warning.main' :
                                      'divider',
                            pl: 1
                        }}
                    >
                        <Typography variant="caption" component="span" sx={{ fontWeight: 'bold' }}>
                            {ts.event}:
                        </Typography>
                        <Typography variant="caption" component="span" sx={{ ml: 1 }}>
                            {formatCompactDate(ts.time)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        );
    };

    return (
        <Container maxWidth="xl" sx={{ pt: 2, pb: 4 }}>
            {/* Page header */}
            <Box sx={{ mb: 3 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link 
                        component={RouterLink} 
                        to="/"
                        color="inherit"
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            color: theme.name === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
                        }}
                    >
                        <HomeIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                        Home
                    </Link>
                    <Link
                        component={RouterLink}
                        to="/inventory"
                        color="inherit"
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            color: theme.name === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
                        }}
                    >
                        Inventory
                    </Link>
                    <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                        <HistoryIcon sx={{ mr: 0.5, fontSize: '1rem' }} />
                        History
                    </Typography>
                </Breadcrumbs>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4" component="h1">
                        Inventory History
                    </Typography>
                    <Tooltip title="Refresh data">
                        <IconButton 
                            onClick={fetchHistoricalData}
                            sx={{ 
                                color: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.2)
                                }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            
            {/* Information card */}
            <Card 
                sx={{ 
                    mb: 3, 
                    bgcolor: theme.name === 'dark' 
                        ? alpha(theme.palette.primary.dark, 0.2) 
                        : alpha(theme.palette.primary.light, 0.1)
                }}
            >
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <InfoOutlinedIcon sx={{ mr: 1 }} /> About Inventory History
                    </Typography>
                    <Typography variant="body2">
                        This page shows the complete history of all inventory items, including changes to device information, 
                        labels, comments, and events. Use the filters below to narrow down the results. 
                        Click on column headers to sort the data.
                    </Typography>
                </CardContent>
            </Card>
            
            {/* Filter panel */}
            <Paper 
                elevation={theme.name === 'dark' ? 3 : 1}
                sx={{ 
                    mb: 3, 
                    p: 2,
                    backgroundColor: theme.name === 'dark' 
                        ? alpha(theme.palette.background.paper, 0.7) 
                        : theme.palette.background.paper
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FilterAltIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="h6">Filters</Typography>
                </Box>
                
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Box Number"
                            placeholder="Filter by box number..."
                            variant="outlined"
                            size="small"
                            value={boxFilter}
                            onChange={(e) => {
                                setBoxFilter(e.target.value);
                                setPage(0);
                            }}
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="operator-filter-label">Operator</InputLabel>
                            <Select
                                labelId="operator-filter-label"
                                value={operatorFilter}
                                onChange={(e) => {
                                    setOperatorFilter(e.target.value);
                                    setPage(0);
                                }}
                                input={<OutlinedInput label="Operator" />}
                            >
                                <MenuItem value="">
                                    <em>All Operators</em>
                                </MenuItem>
                                {uniqueOperators.map((operator) => (
                                    <MenuItem key={operator} value={operator}>
                                        {operator}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Button 
                            variant="outlined" 
                            onClick={resetFilters}
                            startIcon={<ClearAllIcon />}
                            fullWidth
                        >
                            Reset Filters
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
            
            {/* Results count and loading state */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1">
                    Found {sortedAndFilteredDevices.length} records
                </Typography>
                {loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">Loading data...</Typography>
                    </Box>
                )}
            </Box>
            
            {/* Error alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}
            
            {/* Results table */}
            <Paper 
                elevation={theme.name === 'dark' ? 3 : 1}
                sx={{ 
                    overflow: 'hidden',
                    background: theme.name === 'dark' 
                        ? theme.palette.background.paper 
                        : theme.palette.background.paper
                }}
            >
                <TableContainer>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell 
                                    sx={{ 
                                        backgroundColor: theme.name === 'dark' 
                                            ? alpha(theme.palette.primary.dark, 0.2) 
                                            : alpha(theme.palette.primary.light, 0.1),
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {renderSortButton('id', 'ID')}
                                </TableCell>
                                <TableCell 
                                    sx={{ 
                                        backgroundColor: theme.name === 'dark' 
                                            ? alpha(theme.palette.primary.dark, 0.2) 
                                            : alpha(theme.palette.primary.light, 0.1),
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {renderSortButton('box', 'Box Number')}
                                </TableCell>
                                <TableCell 
                                    sx={{ 
                                        backgroundColor: theme.name === 'dark' 
                                            ? alpha(theme.palette.primary.dark, 0.2) 
                                            : alpha(theme.palette.primary.light, 0.1),
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {renderSortButton('iccid', 'ICCID')}
                                </TableCell>
                                <TableCell 
                                    sx={{ 
                                        backgroundColor: theme.name === 'dark' 
                                            ? alpha(theme.palette.primary.dark, 0.2) 
                                            : alpha(theme.palette.primary.light, 0.1),
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {renderSortButton('mobile_operator', 'Operator')}
                                </TableCell>
                                <TableCell 
                                    sx={{ 
                                        backgroundColor: theme.name === 'dark' 
                                            ? alpha(theme.palette.primary.dark, 0.2) 
                                            : alpha(theme.palette.primary.light, 0.1),
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {renderSortButton('modem', 'Modem')}
                                </TableCell>
                                <TableCell 
                                    sx={{ 
                                        backgroundColor: theme.name === 'dark' 
                                            ? alpha(theme.palette.primary.dark, 0.2) 
                                            : alpha(theme.palette.primary.light, 0.1),
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Labels
                                </TableCell>
                                <TableCell 
                                    sx={{ 
                                        backgroundColor: theme.name === 'dark' 
                                            ? alpha(theme.palette.primary.dark, 0.2) 
                                            : alpha(theme.palette.primary.light, 0.1),
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Comments
                                </TableCell>
                                <TableCell 
                                    sx={{ 
                                        backgroundColor: theme.name === 'dark' 
                                            ? alpha(theme.palette.primary.dark, 0.2) 
                                            : alpha(theme.palette.primary.light, 0.1),
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {renderSortButton('time_stamp', 'Timestamp')}
                                </TableCell>
                                <TableCell 
                                    sx={{ 
                                        backgroundColor: theme.name === 'dark' 
                                            ? alpha(theme.palette.primary.dark, 0.2) 
                                            : alpha(theme.palette.primary.light, 0.1),
                                        fontWeight: 'bold',
                                        minWidth: '200px'
                                    }}
                                >
                                    Events
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                        <CircularProgress size={40} />
                                        <Typography variant="body2" sx={{ mt: 2 }}>
                                            Loading history data...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : sortedAndFilteredDevices.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body1">
                                            No matching history records found.
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Try adjusting your filters or refresh the data.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedAndFilteredDevices
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((device) => (
                                        <TableRow 
                                            key={device.id}
                                            hover
                                            sx={{ 
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.name === 'dark' 
                                                        ? alpha(theme.palette.background.paper, 0.15)
                                                        : alpha(theme.palette.background.default, 0.4)
                                                }
                                            }}
                                        >
                                            <TableCell>{device.id}</TableCell>
                                            <TableCell>
                                                <Link
                                                    component={RouterLink}
                                                    to={`/device/${device.box}`}
                                                    sx={{ 
                                                        fontWeight: 'bold',
                                                        color: theme.name === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
                                                    }}
                                                >
                                                    {device.box}
                                                </Link>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {device.iccid || '-'}
                                            </TableCell>
                                            <TableCell>
                                                {device.mobile_operator || device.operator ? (
                                                    <Typography 
                                                        sx={{ 
                                                            color: getOperatorColor(
                                                                (device.mobile_operator || device.operator || 'Unknown')
                                                                .replace('ORANGE', 'Orange')
                                                                .replace('PLAY', 'Play')
                                                                .replace('PLUS', 'Plus')
                                                            ),
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {device.mobile_operator || device.operator}
                                                    </Typography>
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {device.modem || '-'}
                                            </TableCell>
                                            <TableCell>{renderLabels(device.label)}</TableCell>
                                            <TableCell sx={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {device.comments || '-'}
                                            </TableCell>
                                            <TableCell>{device.time_stamp ? formatCompactDate(device.time_stamp) : '-'}</TableCell>
                                            <TableCell>{renderTimeStamps(device.time_stamps)}</TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={sortedAndFilteredDevices.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    sx={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.name === 'dark' 
                            ? alpha(theme.palette.background.default, 0.2)
                            : alpha(theme.palette.background.default, 0.5)
                    }}
                />
            </Paper>
        </Container>
    );
};

export default InventoryHistory; 